import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";

import { API_BASE } from "./config";
import * as api from "./api";
import { pm2Key, reconcileProcs } from "./utils/pm2";
import { UIProvider, useUI } from "./context/UIContext";
import { AuthProvider, useAuth } from "./context/AuthContext";

import LoginPage from "./components/LoginPage";
import UserManagementModal from "./components/modals/UserManagementModal";
import Nav from "./components/Nav";
import StatusBar from "./components/StatusBar";
import ServerMetricsView from "./components/views/ServerMetricsView";
import AppsAlertsView from "./components/views/AppsAlertsView";
import ControlsView from "./components/views/ControlsView";
import Pm2View from "./components/views/Pm2View";
import DeployView from "./components/views/DeployView";
import DocsView from "./components/views/DocsView";

import ServerFormModal from "./components/modals/ServerFormModal";
import DeleteServerModal from "./components/modals/DeleteServerModal";
import ShellModal from "./components/modals/ShellModal";
import Pm2LogModal from "./components/modals/Pm2LogModal";
import PasswdModal from "./components/modals/PasswdModal";
import RestartModal from "./components/modals/RestartModal";
import ShutdownModal from "./components/modals/ShutdownModal";

function fmtClock(withSeconds) {
  const now = new Date();
  const parts = [now.getHours(), now.getMinutes()];
  if (withSeconds) parts.push(now.getSeconds());
  return parts.map((v) => String(v).padStart(2, "0")).join(":");
}

function normalizeProc(p) {
  return {
    name: p.name,
    pmId: p.pmId,
    status: p.status,
    pid: p.pid,
    cpuPct: p.cpuPct || 0,
    memMB:
      p.memMB != null
        ? p.memMB
        : p.memBytes
        ? +(p.memBytes / 1048576).toFixed(1)
        : 0,
    restarts: p.restarts != null ? p.restarts : p.restartCount || 0,
    uptime: p.uptime || 0,
  };
}

// Hợp nhất danh sách app thường (Type='process') từ agent với thao tác đang chờ.
// Sau khi bấm Start/Stop/Restart, các nhịp metrics_apps định kỳ vẫn báo trạng thái
// CŨ vài giây (agent chưa poll lại) → giữ isRunning "kỳ vọng" tới khi agent xác
// nhận hoặc hết hạn, tránh UI nhấp nháy về trạng thái sai.
function reconcileApps(incoming, serverCode, pending, now = Date.now()) {
  return (incoming || []).map((a) => {
    const key = `${serverCode}::${a.appName}`;
    const pend = pending[key];
    if (!pend) return a;
    if (pend.until < now) {
      delete pending[key]; // hết hạn → tin agent
      return a;
    }
    if (a.isRunning === pend.expected) {
      delete pending[key]; // agent xác nhận
      return a;
    }
    return { ...a, isRunning: pend.expected }; // còn cũ → giữ optimistic
  });
}

function Dashboard() {
  const { toast, openModal, closeModal, addAudit } = useUI();
  const { role, isAdmin, canOperate, user, logout } = useAuth();

  // ── State ──────────────────────────────────────────────────────────
  const [theme, setTheme] = useState(
    () => localStorage.getItem("srvmon-theme") || "dark"
  );
  const [activeView, setActiveView] = useState(0);
  const [servers, setServers] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [connectError, setConnectError] = useState(false);
  const [pm2Servers, setPm2Servers] = useState([]);
  const [relay, setRelay] = useState({
    color: "var(--orange)",
    text: "Đang kết nối...",
    url: "",
  });
  const [clock, setClock] = useState(fmtClock(true));
  const [sync, setSync] = useState("—");
  const [searchTerm, setSearchTerm] = useState("");
  const searchRef = useRef(null);

  const critCount = useMemo(() => {
    return alerts.filter((a) => a.severity === "critical").length;
  }, [alerts]);

  const pm2Total = useMemo(() => {
    let sum = 0;
    pm2Servers.forEach((s) => {
      sum += (s.processes || []).length;
    });
    return sum;
  }, [pm2Servers]);



  const serverCount = Object.keys(servers).length;

  // ── Refs (avoid stale closures inside socket / intervals) ───────────
  const serversRef = useRef(servers);
  const pm2Ref = useRef(pm2Servers);
  const lastSyncRef = useRef(0);
  const actionInProgress = useRef(false);
  const pendingPm2 = useRef({}); // `${serverCode}::${name}` → { expected, until }
  const pendingApps = useRef({}); // `${serverCode}::${appName}` → { expected: isRunning, until }
  useEffect(() => {
    serversRef.current = servers;
  }, [servers]);
  useEffect(() => {
    pm2Ref.current = pm2Servers;
  }, [pm2Servers]);

  const markSync = useCallback(() => {
    lastSyncRef.current = Date.now();
  }, []);

  // ── Theme ──────────────────────────────────────────────────────────
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("srvmon-theme", theme);
  }, [theme]);
  const toggleTheme = () =>
    setTheme((t) => (t === "light" ? "dark" : "light"));

  // ── Loaders ────────────────────────────────────────────────────────
  const loadServers = useCallback(async () => {
    try {
      const data = await api.getServers();
      setConnectError(false);
      setServers((prev) => {
        const next = { ...prev };
        data.forEach((sv) => {
          const code = sv.ServerCode;
          next[code] = {
            ...sv,
            metrics: sv.latestMetrics || {},
            apps: prev[code]?.apps || [],
          };
        });
        return next;
      });
    } catch (err) {
      console.error("[API] loadServers:", err);
      setConnectError(true);
      toast("Không kết nối BackendNode", "err", 6000);
    }
  }, [toast]);

  const loadAlerts = useCallback(async () => {
    try {
      const data = await api.getAlerts(50);
      setAlerts(data);
    } catch (err) {
      console.error("[API] loadAlerts:", err);
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  const loadPm2 = useCallback(async () => {
    try {
      const data = await api.getPm2();
      const list = (data.servers || []).map((srv) => ({
        serverCode: srv.serverCode,
        isConnected: srv.isConnected,
        processes: (Array.isArray(srv.processes) ? srv.processes : []).map(
          normalizeProc
        ),
      }));
      setPm2Servers((prev) =>
        list.map((srv) => {
          const prevSrv = prev.find((s) => s.serverCode === srv.serverCode);
          return {
            ...srv,
            processes: reconcileProcs(
              prevSrv?.processes,
              srv.processes,
              srv.serverCode,
              pendingPm2.current
            ),
          };
        })
      );
    } catch (err) {
      console.error("[API] loadPm2:", err);
    }
  }, []);

  // ── Socket.IO ──────────────────────────────────────────────────────
  useEffect(() => {
    const socket = io(API_BASE, {
      query: { role: "dashboard" },
      transports: ["polling", "websocket"],
    });

    socket.on("connect", () => {
      setRelay({
        color: "var(--green)",
        text: "RELAY: Connected",
        url: API_BASE,
      });
      markSync();
    });
    socket.on("disconnect", () =>
      setRelay((r) => ({
        ...r,
        color: "var(--red)",
        text: "RELAY: Disconnected",
      }))
    );
    socket.on("connect_error", () =>
      setRelay((r) => ({ ...r, color: "var(--red)", text: "RELAY: Error" }))
    );

    socket.on("dashboard_metrics", (data) => {
      const code = data.serverCode;
      if (!serversRef.current[code]) {
        loadServers();
        return;
      }
      setServers((prev) => {
        const sv = prev[code];
        if (!sv) return prev;
        return {
          ...prev,
          [code]: {
            ...sv,
            metrics: { ...sv.metrics, ...data },
            IsOnline: 1,
            isConnected: true,
          },
        };
      });
      markSync();
    });

    socket.on("dashboard_apps", (data) => {
      const code = data.serverCode;
      const apps = reconcileApps(data.apps || [], code, pendingApps.current);
      setServers((prev) =>
        prev[code]
          ? { ...prev, [code]: { ...prev[code], apps } }
          : prev
      );
      markSync();
    });

    socket.on("dashboard_pm2", (data) => {
      const code = data.serverCode;
      const incoming = (data.apps || []).map(normalizeProc);
      setPm2Servers((prev) => {
        const idx = prev.findIndex((s) => s.serverCode === code);
        const prevProcs = idx === -1 ? [] : prev[idx].processes;
        const processes = reconcileProcs(
          prevProcs,
          incoming,
          code,
          pendingPm2.current
        );
        const entry = { serverCode: code, isConnected: true, processes };
        if (idx === -1) return [...prev, entry];
        const next = [...prev];
        next[idx] = { ...next[idx], ...entry };
        return next;
      });
      markSync();
    });

    // Alert mới được tạo ở backend → fetch lại danh sách ngay (không chờ poll 30s)
    socket.on("alerts_changed", () => loadAlerts());

    socket.on("server_offline", (data) => {
      const code = data.serverCode;
      setServers((prev) =>
        prev[code]
          ? {
              ...prev,
              [code]: { ...prev[code], IsOnline: 0, isConnected: false },
            }
          : prev
      );
      toast(`${code} ngắt kết nối`, "warn");
    });

    return () => socket.disconnect();
  }, []); // eslint-disable-line

  // ── Initial load + polling ─────────────────────────────────────────
  useEffect(() => {
    loadServers();
    loadAlerts();
    const a = setInterval(loadAlerts, 30 * 1000);
    const s = setInterval(loadServers, 60 * 1000);
    return () => {
      clearInterval(a);
      clearInterval(s);
    };
  }, [loadServers, loadAlerts]);

  // Load PM2 khi mở tab PM2 (3) hoặc tab Ứng dụng & Cảnh báo (1) — cả 2 đều hiển thị PM2
  useEffect(() => {
    if (activeView === 3 || activeView === 1) loadPm2();
  }, [activeView, loadPm2]);

  // ── Clock + sync display ───────────────────────────────────────────
  useEffect(() => {
    const t = setInterval(() => {
      setClock(fmtClock(true));
      if (!lastSyncRef.current) {
        setSync("—");
        return;
      }
      const s = Math.round((Date.now() - lastSyncRef.current) / 1000);
      setSync(s < 60 ? s + "s ago" : Math.floor(s / 60) + "m ago");
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // ── Keyboard shortcuts ─────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (searchRef.current) searchRef.current.focus();
        return;
      }
      if (["INPUT", "SELECT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "1") setActiveView(0);
      if (e.key === "2") setActiveView(1);
      if (e.key === "3") setActiveView(2);
      if (e.key === "4") setActiveView(3);
      if (e.key === "5") setActiveView(4);
      if (e.key === "6" && isAdmin) setActiveView(5);
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeModal, isAdmin]);

  // ── Server CRUD modals ─────────────────────────────────────────────
  const removeServer = useCallback((code) => {
    setServers((prev) => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
  }, []);

  // Optimistic: hiển thị ngay server vừa lưu rồi mới đồng bộ lại từ backend.
  const onServerSaved = useCallback(
    (sv) => {
      if (sv?.ServerCode) {
        setServers((prev) => ({
          ...prev,
          [sv.ServerCode]: {
            ...prev[sv.ServerCode],
            ...sv,
            metrics: sv.metrics || prev[sv.ServerCode]?.metrics || {},
            apps: sv.apps || prev[sv.ServerCode]?.apps || [],
          },
        }));
      }
      loadServers();
    },
    [loadServers]
  );

  const openAdd = () =>
    openModal(
      "＋ Thêm máy chủ mới",
      <ServerFormModal isNew onSaved={onServerSaved} />
    );

  const openEdit = (code) => {
    const sv = serversRef.current[code];
    if (!sv) return toast("Không tìm thấy server", "err");
    openModal(
      `✎ Cấu hình · ${code}`,
      <ServerFormModal server={sv} isNew={false} onSaved={onServerSaved} />
    );
  };

  const openDelete = (code) => {
    const sv = serversRef.current[code];
    const name = sv?.ServerName || code;
    openModal(
      `✕ Xoá máy chủ · ${code}`,
      <DeleteServerModal code={code} name={name} onDeleted={removeServer} />
    );
  };

  // ── Control actions ────────────────────────────────────────────────
  const markServerOffline = useCallback((id) => {
    setServers((prev) =>
      prev[id]
        ? { ...prev, [id]: { ...prev[id], IsOnline: 0, isConnected: false } }
        : prev
    );
  }, []);

  const controlActions = {
    shell: (id) => {
      addAudit("SHELL", id, "var(--cyan)");
      openModal(`PowerShell · ${id}`, <ShellModal serverCode={id} />);
    },
    passwd: (id) =>
      openModal(
        `Đổi mật khẩu · ${id}`,
        <PasswdModal id={id} onAudit={addAudit} />
      ),
    restart: (id) =>
      openModal(
        `Restart · ${id}`,
        <RestartModal
          id={id}
          onConfirm={(sid) => {
            markServerOffline(sid);
            addAudit("RESTART", sid, "var(--orange)");
          }}
        />
      ),
    shutdown: (id) =>
      openModal(
        `Shutdown · ${id}`,
        <ShutdownModal
          id={id}
          onConfirm={(sid) => {
            markServerOffline(sid);
            addAudit("SHUTDOWN", sid, "var(--red)");
          }}
        />
      ),
  };

  // ── PM2 action ─────────────────────────────────────────────────────
  const doPm2Action = useCallback(
    async (serverCode, name, action) => {
      const srv = pm2Ref.current.find((s) => s.serverCode === serverCode);
      const proc = srv?.processes.find((p) => p.name === name);
      if (!proc) return false;

      if (action === "stop" && proc.status !== "online") {
        toast("Không thể Stop - process không online", "warn");
        return false;
      }
      if (action === "start" && proc.status === "online") {
        toast("Không thể Start - process đã online", "warn");
        return false;
      }
      if (actionInProgress.current) return false;
      actionInProgress.current = true;

      try {
        const { ok, data } = await api.pm2Action(serverCode, name, action);
        if (!ok) throw new Error(data.error || data.message || "Lỗi");

        // Khoá trạng thái kỳ vọng tới khi agent xác nhận (agent poll tối đa ~6s
        // rồi mới emit; trong lúc đó có bản metrics_pm2 định kỳ status cũ).
        const expected = action === "stop" ? "stopped" : "online";
        pendingPm2.current[pm2Key(serverCode, name)] = {
          expected,
          until: Date.now() + 15000,
        };

        // Optimistic update — reconcileProcs sẽ giữ status này tới khi xác nhận
        setPm2Servers((prev) =>
          prev.map((s) =>
            s.serverCode !== serverCode
              ? s
              : {
                  ...s,
                  processes: s.processes.map((p) =>
                    p.name === name ? { ...p, status: expected } : p
                  ),
                }
          )
        );
        actionInProgress.current = false;
        toast(`Đã gửi lệnh ${action} ${name} ✅`, "ok");

        // Fallback an toàn: nếu socket miss, đồng bộ lại sau 8s
        setTimeout(() => loadPm2(), 8000);
        return true;
      } catch (err) {
        actionInProgress.current = false;
        toast("Lỗi: " + err.message, "err");
        return false;
      }
    },
    [toast, loadPm2]
  );

  // Mở modal xem nội dung console (log) của 1 app node
  const doPm2Logs = useCallback(
    (serverCode, name) => {
      addAudit("LOG", serverCode, "var(--cyan)");
      openModal(
        `Console · ${name}`,
        <Pm2LogModal serverCode={serverCode} name={name} />,
        "wide"
      );
    },
    [openModal, addAudit]
  );

  // ── Process app action (Type='process') ────────────────────────────
  // Start/Stop/Restart app thường qua agent. Optimistic + pendingApps giữ trạng
  // thái kỳ vọng tới khi agent xác nhận (giống PM2) — tránh nhịp metrics_apps cũ
  // ghi đè ngay sau khi bấm, gây hiện sai trạng thái 1 lúc rồi mới đúng.
  const doProcessAction = useCallback(
    async (serverCode, appName, action) => {
      try {
        const { ok, data } = await api.appAction(serverCode, appName, action);
        if (!ok) {
          toast(data.message || "Lỗi server", "err", 6000);
          return;
        }
        const expected = action !== "stop"; // start/restart → running, stop → down
        pendingApps.current[`${serverCode}::${appName}`] = {
          expected,
          until: Date.now() + 15000,
        };
        setServers((prev) => {
          const sv = prev[serverCode];
          if (!sv) return prev;
          return {
            ...prev,
            [serverCode]: {
              ...sv,
              apps: (sv.apps || []).map((a) =>
                a.appName === appName ? { ...a, isRunning: expected } : a
              ),
            },
          };
        });
        toast(`Đã gửi lệnh ${action} ${appName} ✅`, "ok", 3000);
      } catch {
        toast("Không kết nối được BackendNode", "err");
      }
    },
    [toast]
  );

  // ── Derived ────────────────────────────────────────────────────────
  const meta = {
    0: ["Infrastructure", "Overview", "Fleet Overview"],
    1: ["Infrastructure", "Apps & Alerts", "Applications & Alerts"],
    2: ["Operations", "Controls", "Remote Controls"],
    3: ["Operations", "Processes", "Node Processes"],
    4: ["Operations", "Deploy", "Deploy NodeApp"],
    5: ["Resources", "Docs", "Documentation"],
  }[activeView] || ["", "", ""];
  const crumbA = meta[0];
  const crumbB = meta[1];
  const pageTitle = meta[2];

  return (
    <div className="app">
      <Nav
        activeView={activeView}
        onSwitch={setActiveView}
        alertBadge={critCount}
        pm2Badge={pm2Total}
        user={user}
        role={role}
        isAdmin={isAdmin}
        onLogout={logout}
        onManageUsers={
          isAdmin
            ? () =>
                openModal(
                  "⚿ Quản lý tài khoản",
                  <UserManagementModal />,
                  "wide"
                )
            : null
        }
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, height: "100%" }}>
        {/* Header */}
        <header style={{
          height: "64px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: "16px",
          padding: "0 24px",
          background: "var(--surface)",
          borderBottom: "1px solid var(--border)",
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "11.5px", color: "var(--faint)", fontWeight: 500 }}>
              <span>{crumbA}</span><span>/</span><span style={{ color: "var(--dim)" }}>{crumbB}</span>
            </div>
            <div style={{ fontSize: "18px", fontWeight: 700, letterSpacing: "-.3px", color: "var(--text)" }}>{pageTitle}</div>
          </div>
          
          <div style={{ flex: 1 }}></div>

          {/* Search */}
          <div style={{ display: "flex", alignItems: "center", gap: "9px", padding: "0 13px", height: "38px", borderRadius: "10px", background: "var(--surface2)", border: "1px solid var(--border)", width: "220px" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--faint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            <input
              ref={searchRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              style={{
                background: "transparent",
                border: "none",
                outline: "none",
                color: "var(--text)",
                fontSize: "13px",
                width: "120px",
                padding: 0,
              }}
            />
            {searchTerm ? (
              <span 
                onClick={() => setSearchTerm("")} 
                style={{ marginLeft: "auto", cursor: "pointer", color: "var(--faint)", fontSize: "12px", padding: "2px 5px" }}
              >✕</span>
            ) : (
              <span style={{ marginLeft: "auto", fontSize: "10px", fontFamily: "var(--font-mono)", color: "var(--faint)", border: "1px solid var(--border2)", borderRadius: "5px", padding: "1px 5px" }}>⌘K</span>
            )}
          </div>

          {/* Environment */}
          <div style={{ display: "flex", alignItems: "center", gap: "7px", padding: "0 12px", height: "38px", borderRadius: "10px", background: "var(--surface2)", border: "1px solid var(--border)", fontSize: "13px", fontWeight: 600, color: "var(--text)", cursor: "pointer" }}>
            <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "var(--green)" }}></span>Production
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--faint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: "2px" }}><path d="M6 9l6 6 6-6"></path></svg>
          </div>

          {/* Live Status indicator */}
          <div style={{ display: "flex", alignItems: "center", gap: "7px", padding: "0 11px", height: "38px", borderRadius: "10px", background: "var(--green-soft)", fontSize: "12px", fontWeight: 600, fontFamily: "var(--font-mono)", color: "var(--green)" }}>
            <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "var(--green)", animation: "livePulse 1.5s infinite" }}></span>LIVE
          </div>

          {/* Clock */}
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "14px", fontWeight: 600, color: "var(--dim)", minWidth: "74px", textAlign: "right" }}>{clock}</div>

          {/* Theme toggle button */}
          <button 
            onClick={toggleTheme} 
            style={{ width: "38px", height: "38px", borderRadius: "10px", background: "var(--surface2)", border: "1px solid var(--border)", color: "var(--dim)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            className="header-btn"
            title="Chuyển theme"
          >
            {theme === "dark" ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"></circle><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"></path></svg>
            )}
          </button>
        </header>

        {/* View Content */}
        <div className="view-content scroll">
          {activeView === 0 && (
            <ServerMetricsView
              servers={servers}
              pm2Servers={pm2Servers}
              alertCount={alerts.length || 0}
              connectError={connectError}
              canEdit={isAdmin}
              onAdd={openAdd}
              onEdit={openEdit}
              onDelete={openDelete}
              searchTerm={searchTerm}
            />
          )}
          {activeView === 1 && (
            <AppsAlertsView
              servers={servers}
              pm2Servers={pm2Servers}
              onPm2Action={doPm2Action}
              onProcessAction={doProcessAction}
              alerts={alerts}
              alertsLoading={alertsLoading}
              canOperate={canOperate}
              isAdmin={isAdmin}
              searchTerm={searchTerm}
            />
          )}
          {activeView === 2 &&
            (isAdmin ? (
              <ControlsView servers={servers} pm2Servers={pm2Servers} actions={controlActions} searchTerm={searchTerm} />
            ) : (
              <div className="empty-state" style={{ marginTop: 40 }}>
                🔒 Chức năng Điều khiển (remote, restart, shutdown, đổi mật khẩu) chỉ dành cho Admin.
              </div>
            ))}
          {activeView === 3 && (
            <Pm2View
              pm2Servers={pm2Servers}
              loadPm2={loadPm2}
              onAction={doPm2Action}
              onLogs={doPm2Logs}
              canOperate={canOperate}
              searchTerm={searchTerm}
            />
          )}
          {activeView === 4 && (
            <DeployView servers={servers} isAdmin={isAdmin} />
          )}
          {activeView === 5 && isAdmin && <DocsView />}
        </div>

        <StatusBar
          relay={relay}
          clock={clock}
          serverCount={serverCount || "—"}
          sync={sync}
        />
      </div>
      
      <style>{`
        .header-btn:hover {
          color: var(--text) !important;
          border-color: var(--border2) !important;
        }
      `}</style>
    </div>
  );
}

// Cổng vào: chưa quyết định (chưa đăng nhập & chưa chọn guest) → trang đăng nhập.
function Root() {
  const { ready, authed } = useAuth();
  if (!ready)
    return (
      <div className="login-wrap" style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: "var(--bg)",
      }}>
        <div style={{ color: "var(--dim)", fontFamily: "var(--font-mono)", fontSize: "14px" }}>
          Đang tải…
        </div>
      </div>
    );
  if (!authed) return <LoginPage />;
  return <Dashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <UIProvider>
        <Root />
      </UIProvider>
    </AuthProvider>
  );
}
